/**
 * useMissionTraces — Hook pour récupérer les traces + SSE temps réel
 */

import { useState, useEffect, useRef } from "react";

export interface Trace {
  id: string;
  missionId: string;
  attemptNumber: number;
  agentId?: string;
  agentName: string;
  agentRole?: string;
  actionType: "code_write" | "search" | "test_run" | "question" | "decision";
  description: string;
  status: "success" | "error" | "running";
  artifacts: string[];
  errorMessage?: string;
  durationMs?: number | null;
  createdAt: string;
}

const BASE = import.meta.env.VITE_API_URL || "/api";

export function useMissionTraces(missionId: string | null) {
  const [traces, setTraces] = useState<Trace[]>([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Load existing traces + open SSE
  useEffect(() => {
    if (!missionId) return;

    setLoading(true);

    // Load existing traces
    fetch(`${BASE}/missions/${missionId}/traces`)
      .then((res) => res.json())
      .then((data: Trace[]) => {
        setTraces(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Open SSE stream for live updates
    const sse = new EventSource(`${BASE}/missions/${missionId}/stream`);
    eventSourceRef.current = sse;

    sse.onopen = () => setConnected(true);
    sse.onerror = () => setConnected(false);
    sse.onmessage = (event) => {
      try {
        const trace = JSON.parse(event.data) as Trace;
        setTraces((prev) => [...prev, trace]);
      } catch {
        // ignore parse errors
      }
    };

    return () => {
      sse.close();
      eventSourceRef.current = null;
      setConnected(false);
    };
  }, [missionId]);

  return { traces, loading, connected };
}
